"use client";

import { type ReactNode, useEffect, useRef } from "react";

/**
 * Aparición suave al entrar en pantalla. Se anima una sola vez por elemento y después se
 * desconecta el observador — nada de listeners de scroll ni de recalcular en cada cuadro.
 *
 * Si `reveal-on` no está en el `<html>` (sin JavaScript en el arranque, sin `IntersectionObserver`,
 * o el sistema pide menos movimiento) el elemento ya se ve por CSS y esto no hace nada.
 */
export function Reveal({
  children,
  className,
  /** Retraso en ms, para escalonar los elementos de una grilla. */
  retraso = 0,
  /**
   * De dónde entra. `abajo` es el de siempre y sigue siendo el que hay que usar por defecto: los otros
   * son para cuando el elemento tiene un lado natural —una columna de texto junto a una imagen, una
   * tarjeta que crece— y se notan de más si se reparten al azar.
   */
  desde = "abajo",
}: {
  children: ReactNode;
  className?: string;
  retraso?: number;
  desde?: "abajo" | "izquierda" | "derecha" | "escala";
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!document.documentElement.classList.contains("reveal-on")) return;

    const observador = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          if (entrada.isIntersecting) {
            el.dataset.visible = "true";
            observador.disconnect();
          }
        }
      },
      // Se dispara un poco antes de que el elemento toque el borde inferior, para que llegue
      // animado y no "saltando" justo al aparecer.
      { rootMargin: "0px 0px -12% 0px", threshold: 0.05 },
    );

    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-reveal={desde}
      className={className}
      style={retraso ? { transitionDelay: `${retraso}ms` } : undefined}
    >
      {children}
    </div>
  );
}
